import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPartArrangementStatusOptions,
  WfPartArrangementStatus,
  WfPartArrangementStatusMapping,
} from 'src/api/wf/models/enums/WfPartArrangementStatus';
import Input from 'src/ui/wf/common/input/Input';
import DropdownSelect from 'src/ui/wf/common/select/DropdownSelect';
import TextArea from 'src/ui/wf/common/textArea/TextArea';
import PhotoGrid from './PhotoGrid';
import { FormState, PhotoManager } from './hooks';
import { FormRow } from '../../form/FormRow';

interface PartRequiredFieldsProps {
  formState: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  photoManager: PhotoManager;
}

export const PartRequiredFields = ({
  formState,
  onFieldChange,
  photoManager,
}: PartRequiredFieldsProps) => {
  const { t } = useTranslation();
  const arrangementStatus =
    formState.arrangement === WfPartArrangementStatus.Arranged
      ? WfPartArrangementStatus.Arranged
      : WfPartArrangementStatus.NotArranged;

  return (
    <div className="block space-y-2 border bg-green-100 p-2">
      <h3 className="mb-4 block text-lg font-semibold">
        {t('wf.statusInformationRegistration.partArrangementInfo')}
      </h3>

      <FormRow title={t('wf.statusInformationRegistration.partNumber')}>
        <Input
          value={formState.partsNumber}
          onChange={(e) => onFieldChange('partsNumber', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.partName')}>
        <Input
          value={formState.partsName}
          onChange={(e) => onFieldChange('partsName', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.quantity')}>
        <Input
          value={formState.quantity}
          onChange={(e) => onFieldChange('quantity', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.arrangement')}>
        <DropdownSelect
          selectedValue={WfPartArrangementStatusMapping(t)[arrangementStatus]}
          optionValues={getPartArrangementStatusOptions(t)}
          onChange={(e) => onFieldChange('arrangement', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.deliveryAddress')}>
        <Input
          value={formState.deliveryAddress}
          onChange={(e) => onFieldChange('deliveryAddress', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.eta')}>
        <Input
          value={formState.estimatedArrivalTime}
          onChange={(e) => onFieldChange('estimatedArrivalTime', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.requestNumber')}>
        <Input
          value={formState.requestNumber}
          onChange={(e) => onFieldChange('requestNumber', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.workOrderNumber')}>
        <Input
          value={formState.workOrderNumber}
          onChange={(e) => onFieldChange('workOrderNumber', e.target.value)}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.remarks')}>
        <TextArea
          value={formState.partsComments ?? ''}
          onChange={(val) => onFieldChange('partsComments', val)}
          rows={3}
        />
      </FormRow>

      {/* Photo Section */}
      <PhotoGrid photoManager={photoManager} themeColor="green" />
    </div>
  );
};
